import { useCallback, useEffect, useState } from 'react';

/**
 * Three states, not two. "system" stamps nothing on <html>, which is what lets
 * prefers-color-scheme decide; an explicit choice stamps data-theme so it beats
 * the operating system in either direction (PLAN §13.1 asks for system default
 * with a toggle in Settings).
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'dintiko.theme';

function read(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing can refuse localStorage; falling back to system is fine.
  }
  return 'system';
}

function apply(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/**
 * Stamp the stored choice on <html> before React mounts.
 *
 * `apply` used to run only inside useTheme's effect, and useTheme is called on
 * exactly two screens — Settings and the dev primitives page. So choosing dark
 * wrote to localStorage and then did nothing on every subsequent cold start
 * until the owner happened to open Settings again. The toggle appeared to
 * forget.
 *
 * Called from main.tsx at module scope, which is why it guards on `document`:
 * the entry point is never imported by a test, but the guard costs nothing and
 * makes the function safe to call from anywhere.
 */
export function applyStoredTheme(): void {
  if (typeof document === 'undefined') return;
  apply(read());
}

export function useTheme(): { choice: ThemeChoice; setChoice: (next: ThemeChoice) => void } {
  const [choice, setStored] = useState<ThemeChoice>(read);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setStored(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisting is survivable; the choice still applies for this session.
    }
  }, []);

  return { choice, setChoice };
}
